import { useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from 'src/models/user/_types';
import { fetchUsers } from 'src/models/user/user';
import { getUserDisplayName } from 'src/models/user/utils';
import { BaseSelectOption } from 'src/shared/Forms/Selects/BaseSelect';
import { TrainerSelect } from 'src/shared/Forms/Selects/TrainerSelect';
import { Identifier } from 'src/types/utilityTypes';

interface TrainerFilterConfig {
  field: string;
  label: string;
  value: string | undefined;
  setValue: (newVal: string | undefined) => void;
  valueOnly?: boolean;
  multiple?: boolean;
  includeIds?: Identifier[];
  filterOptions?: (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => BaseSelectOption[];
}

const normalizeToSelectId = (id: string): Identifier | null => {
  if (!id) return null;
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
}

export const TrainerFilter: React.FC<TrainerFilterConfig> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple,
  includeIds,
  filterOptions,
}) => {
  const queryClient = useQueryClient();
  const trainerIds = value ? String(value).split(',') : [];

  const seededUsers = queryClient.getQueryData<User[]>(['users', trainerIds]);
  const hasSeed = !!seededUsers?.length;

  const { data: users = seededUsers ?? [] } = useQuery<User[]>({
    queryKey: ['users', trainerIds],
    queryFn: () => fetchUsers({ ids: trainerIds as Identifier[] }),
    enabled: !!value && valueOnly && !hasSeed,
    staleTime: 5 * 60 * 1000,
  });

  if (valueOnly) {
    const names = (
      multiple && value
        ? (users ?? []).filter((u) => trainerIds.includes(String(u.id)))
        : users ?? []
    )
      .map((u) => getUserDisplayName(u))
      .join(', ');
    return <>{names || label}</>;
  }

  const selectedValue = multiple ? trainerIds.map(normalizeToSelectId).filter((id): id is Identifier => id != null) : normalizeToSelectId(trainerIds[0] ?? '');

  return (
    <TrainerSelect
      label={label}
      value={selectedValue}
      includeIds={includeIds}
      valueOnly={false}
      onChange={(newValue) => {
        if (multiple) {
          setValue(
            (Array.isArray(newValue) ? newValue : []).map((s) => s.id).join(',')
          );
        } else {
          setValue(String(newValue?.id || ''));
        }
      }}
      placeholder="Any"
      multiple={multiple}
      filterOptions={filterOptions}
    />
  );
};
